import { NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TranslatePipe } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { TypeaheadService } from "~app/common/services/typeahead/typeahead.service";
import { TypeAhead } from "~app/common/services/typeahead/typeahead.types";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IHandelssortimentTypeAhead } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { OrderService } from "~app/features/forsaljning/order/order.service";
import { DisabledControlDirective } from "../../../../../../common/directives/disabled-control/disabled-control.directive";
import { ValidationMessageRowComponent } from "../../../../../../common/validation/validation-message-row/validation-message-row.component";
import { KundorderService } from "../../../kundorder.service";
import { IAlternativtHandelssortiment, IKundorderDetailKundorderrad } from "../../../models/kundorder-detail-model";
import { AddKundorderradAlternativaHandelssortimentCommand, Handelssortiment } from "../commands/AddKundorderradAlternativaHandelssortimentCommand";
import { UpdateKundorderradAlternativaHandelssortimentCommand } from "../commands/UpdateKundorderradAlternativaHandelssortimentCommand";

@Component({
    selector: "rask-alternativa-handelssortiment",
    templateUrl: "./alternativa-handelssortiment.component.html",
    styleUrls: ["./alternativa-handelssortiment.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectorComponent,
        FormsModule,
        DisabledControlDirective,
        ButtonComponent,
        ListComponent,
        ListColumnDirective,
        CheckboxComponent,
        NgClass,
        IconButtonComponent,
        ValidationMessageRowComponent,
        TranslatePipe
    ]
})
export class AlternativaHandelssortimentComponent {
    @Input() alternativtHandelssortiment: IAlternativtHandelssortiment[];
    errors: string[] = [];
    @Input() kundorderrad: IKundorderDetailKundorderrad;
    @Input() kundorderradId: number;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onChange = new EventEmitter();
    @Input() readonly = false;
    selected: IHandelssortimentTypeAhead;
    validationTexts =[];
    private readonly authenticationService = inject(AuthenticationService);
    private readonly commandService = inject(CommandService);
    private readonly kundorderService = inject(KundorderService);
    private readonly orderService = inject(OrderService);
    private readonly typeaheadService = inject(TypeaheadService);

    constructor() {
        this.kundorderService.alternativaHandelssortimentErrors$.subscribe(x => {
            this.errors = x;
        });
    }

    add() {
        if (!this.selected || !this.selected.id) {
            return;
        }

        this.alternativtHandelssortiment.push({
            id: undefined,
            handelssortimentId: this.selected.id,
            egenskapskodX: false,
            endastCentralaKop: false,
            manuellPaAo: false,
            namn: this.selected.namn,
            inmatningshandelssortiment: null,
            beraknatInmatningshandelssortiment: this.selected.beraknatInmatningshandelssortiment,
            clientIdGuid: GuidUtils.newGuid()
        });

        this.alternativtHandelssortiment = this.alternativtHandelssortiment.splice(0);
        this.selected = null;
        this.onChange.emit(this.alternativtHandelssortiment);
        this.kundorderService.validateAlternativaHandelssortiment(this.alternativtHandelssortiment, this.kundorderrad.handelssortiment.id);
    }

    canAdd() {
        const isSelected = this.selected && this.selected.id;
        return isSelected && (!this.alternativtHandelssortiment.some(x => x.handelssortimentId === this.selected.id) || this.selected.id === this.kundorderrad.handelssortiment.id);
    }

    canEdit = () => this.authenticationService.canEdit() && !this.readonly;

    canEditEndastCK = (row: IAlternativtHandelssortiment) => this.authenticationService.canEdit() && !this.readonly && !row.egenskapskodX && !row.manuellPaAo;

    delete(item: IAlternativtHandelssortiment) {
        this.alternativtHandelssortiment = this.alternativtHandelssortiment.filter(x => (x.id !== item.id && !ObjectUtils.isNullOrUndefined(item.id)) ||
                                                                                (x.clientIdGuid !== item.clientIdGuid &&  !ObjectUtils.isNullOrUndefined(item.clientIdGuid)));
        this.onChange.emit(this.alternativtHandelssortiment);

        this.removeExistingCommand(item);
    }

    @Input() formatter = (item: ITypeAheadResult) => null;

    handelssortimentFormatter = (item: IHandelssortimentTypeAhead) => item ? item.id : null;

    handelssortimentViewFormatter = (item: IHandelssortimentTypeAhead) => item ? `${item.violId || ""} ${item.namn}` : null;

    onSelectChange(row: IAlternativtHandelssortiment, value) {
        row.inmatningshandelssortiment = value;
        row.inmatningshandelssortimentId = value?.id;
        this.updateCommand(row);
    }

    @Input() search = (input: string): Observable<ITypeAheadResult[]> => of();

    searchForHandelssortiment = (input: string): Observable<IHandelssortimentTypeAhead[]> => input.length < 3
        ? this.getPreload(TypeAhead.Handelssortiment)
        : this.orderService.searchForHandelssortimentTypeAhead(input, null, true);

    updateCommand(row: IAlternativtHandelssortiment) {
        this.kundorderService.validateAlternativaHandelssortiment(this.alternativtHandelssortiment, this.kundorderrad.handelssortiment.id);
        if(row.egenskapskodX || row.manuellPaAo) {
            row.endastCentralaKop = false;
        }
        const updateExisting = this.updateExistingCommand(row);

        if (!updateExisting) {
            const command = new UpdateKundorderradAlternativaHandelssortimentCommand(this.kundorderradId, row.id);
            command.handelssortimentId = row.handelssortimentId;
            command.manuellPaAo = row.manuellPaAo;
            command.egenskapskodX = row.egenskapskodX;
            command.endastCentralaKop = row.endastCentralaKop;
            command.inmatningshandelssortimentId = row.inmatningshandelssortiment?.id;

            this.removeExistingCommand(row);
            this.commandService.addTransactionCommand(command);
        }
    }

    private getPreload<T>(type: string): Observable<T[]> {
        return of(this.typeaheadService.getPreload<T>(type, "Ange minst 3 tecken"));
    }

    private mapHs(hs: Handelssortiment, item: IAlternativtHandelssortiment) {
        if (item.handelssortimentId === hs.handelssortimentId && item.clientIdGuid === hs.clientIdGuid) {
            hs.manuellPaArbetsorder = item.manuellPaAo;
            hs.egenskapskodX = item.egenskapskodX;
            hs.inmatningshandelssortimentId = item.inmatningshandelssortiment?.id;
            hs.endastCentralaKop = item.endastCentralaKop;
            hs.clientIdGuid = item.clientIdGuid;
        }
    }

    private removeExistingCommand(item: IAlternativtHandelssortiment) {
        const allAddCommands = this.commandService.transactionCommandQueue
            .filter(x => x.$type === AddKundorderradAlternativaHandelssortimentCommand.type) as AddKundorderradAlternativaHandelssortimentCommand[];
        const addComand = allAddCommands.find(x => x.handelssortiment.some(y => y.clientIdGuid === item.clientIdGuid));
        if (addComand) {
            addComand.handelssortiment.splice(addComand.handelssortiment.indexOf(addComand.handelssortiment.find(x => x.clientIdGuid === item.clientIdGuid)), 1);
        }

        const updateCommands = this.commandService.transactionCommandQueue.filter(x =>
            x.$type === UpdateKundorderradAlternativaHandelssortimentCommand.type) as UpdateKundorderradAlternativaHandelssortimentCommand[];
        const existingCommand = updateCommands.find(x => x.id === item.id && x.kundorderradId === this.kundorderradId);
        if (existingCommand) {
            this.commandService.removeCommand(existingCommand);
        }
        this.kundorderService.validateAlternativaHandelssortiment(this.alternativtHandelssortiment, this.kundorderrad.handelssortiment.id);
    }

    private updateExistingCommand(item: IAlternativtHandelssortiment): boolean {
        const allAddCommands = this.commandService.transactionCommandQueue
            .filter(x => x.$type === AddKundorderradAlternativaHandelssortimentCommand.type) as AddKundorderradAlternativaHandelssortimentCommand[];

        const rowAddCommands = allAddCommands.filter(x => x.kundorderradId === this.kundorderradId);

        if(!rowAddCommands.some(x => x.handelssortiment.some(y => y.clientIdGuid === item.clientIdGuid))) {
            return false;
        }

        rowAddCommands.forEach(x => {
            x.handelssortiment.forEach(hs => {
                this.mapHs(hs, item);
            });
        });

        return true;
    }
}
